import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
  ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { LatLngExpression } from "leaflet";
import { Box, Typography, Button, Fab, CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import man from "../../assets/man.png";
import { getAccessibilityLevel, AccessibilityLevel } from "@/shared/constants/accessibility";
import useApiClient from "../../api/Client";
import Header from "../../common/Header";
import {
  setSelectedPlace,
  clearSelectedPlace,
} from "../../store/slices/selectedPlaceSlice";
import {
  setDrawerOpen,
  clearPlaceFilter,
} from "@/store/slices/placeFilterSlice";
import AccessibilityGuide from "./AccessibilityGuide";
import BottomNavBar from "@/common/BottomNavBar";
import ENDPOINTS from "@/api/endpoints";
import FilterDrawer from "../filterDrawer";
import { RootState } from "@/store";
import { useAuth } from "../../providers/AuthProvider";
import { Bounds, Place, GoogleNearbyPlace } from "@/types/common";
import { calculateDistance } from "../../utils/helper";
import { createImageIcon, createTextIcon, getCategoryIconUrl } from "../../utils/mapUtils";
import { storage, STORAGE_KEYS } from "../../utils/storage";

interface CombinedMarkerProps {
  position: [number, number];
  type: AccessibilityLevel;
  label: string;
  placeData: Place;
  onMarkerClick: (place: Place) => void;
}

export interface MarkerData {
  position: [number, number];
  type: AccessibilityLevel;
  label: string;
  placeData: Place;
}

export interface MobileMapUIProps {
  mapCenter?: [number, number];
  mapZoom?: number;
  markers?: MarkerData[];
  userPosition?: [number, number];
  onSearch?: (query: string) => void;
  onFilterChange?: (filter: string) => void;
}

const CUSTOM_COLOR = "#9068f6";

const API_CONFIG = {
  DEBOUNCE_DELAY: 300,
};
const overlayStyles = {
  position: "absolute",
  top: 0,
  left: "17.65vw",
  right: 0,
  height: "140px",
  zIndex: 2000,
  pointerEvents: "auto",
};

const componentStyles = {
  container: {
    height: "100vh",
    display: "flex",
    flexDirection: "column" as const,
    backgroundColor: "#f8f9ff",
    maxWidth: { xs: "100vw", sm: "400px" },
    margin: "0 auto",
    boxShadow: { xs: "none", sm: "0 0 20px rgba(0,0,0,0.1)" },
    overflow: "hidden",
  },
  mapContainer: {
    flexGrow: 1,
    position: "relative" as const,
    overflow: "hidden",
    "& .leaflet-container": {
      background: `linear-gradient(135deg, #f5f7ff 0%, ${CUSTOM_COLOR}15 50%, #f0f4ff 100%)`,
      height: "100%",
      width: "100%",
    },
    // Add these styles for zoom control positioning
    "& .leaflet-top.leaflet-left": {
      top: "140px", // Push zoom controls below header
    },
    "& .leaflet-control-zoom": {
      position: "fixed",
      border: `2px solid ${CUSTOM_COLOR}30`,
      borderRadius: "8px",
      overflow: "hidden",
    },
    "& .leaflet-control-zoom a": {
      backgroundColor: "#fff",
      color: CUSTOM_COLOR,
      borderBottom: `1px solid ${CUSTOM_COLOR}20`,
      fontWeight: "bold",
      fontSize: "18px",
    },
    "& .leaflet-control-zoom a:hover": {
      backgroundColor: `${CUSTOM_COLOR}15`,
    },
    "& .leaflet-control-zoom a:first-child": {
      borderRadius: "6px 6px 0 0",
    },
    "& .leaflet-control-zoom a:last-child": {
      borderBottom: "none",
      borderRadius: "0 0 6px 6px",
    },
    "& .text-marker": {
      background: "transparent !important",
      border: "none !important",
    },
    "& .text-marker div": {
      transition: "all 0.2s ease",
    },
    "& .text-marker div:hover": {
      transform: "scale(1.05)",
      boxShadow: `0 4px 12px ${CUSTOM_COLOR}30 !important`,
    },
    "& .leaflet-popup-content-wrapper": {
      borderRadius: "12px",
      border: `2px solid ${CUSTOM_COLOR}`,
      boxShadow: `0 4px 12px ${CUSTOM_COLOR}40`,
    },
    "& .leaflet-popup-tip": {
      backgroundColor: CUSTOM_COLOR,
    },
    "& .leaflet-popup-content": {
      margin: "12px",
      color: "#333",
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  },
  bottomNav: {
    backgroundColor: "#fff",
    borderTop: `2px solid ${CUSTOM_COLOR}`,
    position: "fixed",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    maxWidth: { xs: "100vw", sm: "400px" },
    height: { xs: "60px", sm: "64px" },
    zIndex: 1300,
    boxShadow: "0 -4px 12px rgba(0,0,0,0.1)",
    "& .MuiBottomNavigationAction-root": {
      fontSize: { xs: "12px", sm: "14px" },
      minWidth: { xs: "auto", sm: "80px" },
      padding: { xs: "6px 12px", sm: "8px 12px" },
    },
    "& .MuiBottomNavigationAction-label": {
      fontSize: { xs: "11px", sm: "12px" },
      fontWeight: 500,
      marginTop: { xs: "2px", sm: "4px" },
    },
    "& .MuiBottomNavigationAction-icon": {
      fontSize: { xs: "20px", sm: "24px" },
    },
  },
  recenterButton: {
    position: "fixed",
    bottom: { xs: 80, sm: 84 },
    right: { xs: 16, sm: 20 },
    backgroundColor: CUSTOM_COLOR,
    color: "white",
    width: { xs: 48, sm: 52 },
    height: { xs: 48, sm: 52 },
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    "&:hover": {
      backgroundColor: `${CUSTOM_COLOR}dd`,
    },
    transform: "none",
    margin: 0,
  },
  loadingOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1000,
    background: "rgba(255,255,255,0.95)",
    borderRadius: 3,
    p: { xs: 2, sm: 3 },
    width: { xs: "80%", sm: "420px" },
    boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  },
};

const findNearestPlace = (
  userPosition: [number, number],
  places: Place[]
): string | null => {
  if (!userPosition || places.length === 0) return null;

  let nearestPlace = null;
  let minDistance = Infinity;

  places.forEach((place) => {
    const distance = calculateDistance(
      userPosition[0],
      userPosition[1],
      place.coordinates.lat,
      place.coordinates.lng
    );

    if (distance < minDistance) {
      minDistance = distance;
      nearestPlace = place.place_id;
    }
  });

  return nearestPlace;
};

const getClosestPlaceToShowLabel = (
  userPosition: [number, number],
  nearbyPlaces: MarkerData[]
): string | null => {
  if (!userPosition || nearbyPlaces.length === 0) return null;

  let closestPlaceId = null;
  let minDistance = Infinity;

  nearbyPlaces.forEach((marker) => {
    const distance = calculateDistance(
      userPosition[0],
      userPosition[1],
      marker.position[0],
      marker.position[1]
    );

    if (distance < minDistance) {
      minDistance = distance;
      closestPlaceId = marker.placeData?.place_id || null;
    }
  });

  return closestPlaceId;
};

const createUserIcon = (accuracy?: number): L.DivIcon => {
  const accuracyText = accuracy ? `±${Math.round(accuracy)}m` : "";
  const accuracyColor =
    accuracy && accuracy < 10
      ? "#4CAF50"
      : accuracy && accuracy < 50
      ? "#FF9800"
      : "#F44336";

  return L.divIcon({
    html: `
      <div style="
        position: relative;
        width: 40px;
        height: 40px;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background-color: ${CUSTOM_COLOR}30;
          animation: pulse 2s infinite;
        "></div>
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        ">
          <img 
            src="${man}" 
            alt="User" 
            style="width: 100%; height: 100%; object-fit: contain;" 
          />
        </div>
        ${
          accuracy
            ? `
          <div style="
            position: absolute;
            top: -25px;
            left: 50%;
            transform: translateX(-50%);
            background: ${accuracyColor};
            padding: 2px 6px;
            border-radius: 10px;
            font-size: 10px;
            font-weight: bold;
            white-space: nowrap;
          ">
            ${accuracyText}
          </div>
        `
            : ""
        }
        <style>
          @keyframes pulse {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.8;
            }
            70% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0;
            }
            100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0;
            }
          }
        </style>
      </div>
    `,
    className: "user-marker",
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

function SimplifiedBoundsWatcher({
  onBoundsChange,
}: {
  onBoundsChange: (bounds: Bounds, source: "drag" | "zoom" | "move") => void;
}) {
  const map = useMap();
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastInteractionRef = useRef<"drag" | "zoom" | "move">("move");

  useMapEvents({
    dragstart: () => {
      lastInteractionRef.current = "drag";
    },

    zoomstart: () => {
      lastInteractionRef.current = "zoom";
    },

    zoomend: () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        const bounds = map.getBounds();
        const boundsData = {
          topLeft: {
            lat: bounds.getNorth(),
            lng: bounds.getWest(),
          },
          bottomRight: {
            lat: bounds.getSouth(),
            lng: bounds.getEast(),
          },
        };
        onBoundsChange(boundsData, "zoom");
      }, API_CONFIG.DEBOUNCE_DELAY);
    },

    dragend: () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = setTimeout(() => {
        const bounds = map.getBounds();
        const boundsData = {
          topLeft: {
            lat: bounds.getNorth(),
            lng: bounds.getWest(),
          },
          bottomRight: {
            lat: bounds.getSouth(),
            lng: bounds.getEast(),
          },
        };
        onBoundsChange(boundsData, "drag");
      }, API_CONFIG.DEBOUNCE_DELAY);
    },

    moveend: () => {
      if (
        lastInteractionRef.current !== "drag" &&
        lastInteractionRef.current !== "zoom"
      ) {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
          const bounds = map.getBounds();
          const boundsData = {
            topLeft: {
              lat: bounds.getNorth(),
              lng: bounds.getWest(),
            },
            bottomRight: {
              lat: bounds.getSouth(),
              lng: bounds.getEast(),
            },
          };
          onBoundsChange(boundsData, "move");
        }, API_CONFIG.DEBOUNCE_DELAY);
      }

      setTimeout(() => {
        lastInteractionRef.current = "move";
      }, API_CONFIG.DEBOUNCE_DELAY + 100);
    },
  });

  return null;
}

const CombinedMarker: React.FC<
  CombinedMarkerProps & {
    shouldAutoOpen?: boolean;
    isLoading?: boolean;
    isNearest?: boolean;
    userPosition: [number, number] | null;
    showLabel?: boolean;
  }
> = ({
  position,
  type,
  label,
  placeData,
  onMarkerClick,
  shouldAutoOpen = false,
  userPosition,
  showLabel = false,
}) => {
  const markerRef = useRef<L.Marker>(null);

  useEffect(() => {
    if (shouldAutoOpen && markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [shouldAutoOpen]);

  const categoryIconUrl = getCategoryIconUrl(placeData, type);
  const categoryIcon = createImageIcon(categoryIconUrl);

  const isNearUser = userPosition
    ? calculateDistance(
        userPosition[0],
        userPosition[1],
        position[0],
        position[1]
      ) <= 20
    : false;

  const shouldShowText = isNearUser && showLabel;
  const getCloserOffset = () => {
    const baseOffset = 0.00001;
    const randomVariation = (Math.random() - 0.5) * 0.00002;
    return baseOffset + randomVariation;
  };

  const textOffsetLat = getCloserOffset();
  const textOffsetLng = getCloserOffset();

  return (
    <React.Fragment>
      <Marker
        ref={markerRef}
        position={position}
        icon={categoryIcon}
        eventHandlers={{
          click: () => onMarkerClick(placeData),
        }}
      />
      {shouldShowText && (
        <Marker
          position={[position[0] + textOffsetLat, position[1] + textOffsetLng]}
          icon={createTextIcon(label, true, CUSTOM_COLOR)}
          eventHandlers={{
            click: () => onMarkerClick(placeData),
          }}
        />
      )}
    </React.Fragment>
  );
};
function ChangeView({
  center,
  zoom,
  shouldCenter,
}: {
  center: [number, number] | null;
  zoom: number;
  shouldCenter?: boolean;
}) {
  const map = useMap();

  useEffect(() => {
    if (center && shouldCenter) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map, shouldCenter]);

  return null;
}

function MapController({
  userPosition,
  onRecenter,
}: {
  userPosition: [number, number] | null;
  onRecenter: () => void;
}) {
  const map = useMap();

  const handleRecenter = () => {
    if (userPosition) {
      map.setView(userPosition, 18);
      onRecenter();
    }
  };

  return (
    <Fab
      sx={componentStyles.recenterButton}
      onClick={handleRecenter}
      disabled={!userPosition}
      size="medium"
    >
      <MyLocationIcon />
    </Fab>
  );
}

const hasUserSeenPopup = (): boolean => {
  return storage.getItem(STORAGE_KEYS.HAS_SEEN_ACCESSIBILITY_POPUP) === "true";
};

const markPopupAsSeen = (): void => {
  storage.setItem(STORAGE_KEYS.HAS_SEEN_ACCESSIBILITY_POPUP, "true");
};

const Explore: React.FC<MobileMapUIProps> = ({
  mapZoom = 19,
  onFilterChange,
}) => {
  const dispatch = useDispatch();
  const { selectedCategory: selectedFilter } = useSelector(
    (state: RootState) => state.placeFilter
  );
  const { isAuthenticated } = useAuth();
  const [nearestPlaceId, setNearestPlaceId] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [places, setPlaces] = useState<Place[]>([]);
  const [placeWithLabel, setPlaceWithLabel] = useState<string | null>(null);
  const [mapMarkers, setMapMarkers] = useState<MarkerData[]>([]);
  const { api } = useApiClient();
  const [userPosition, setUserPosition] = useState<[number, number] | null>(
    null
  );
  const [userAccuracy, setUserAccuracy] = useState<number | null>(null);
  const [initialMapCenter, setInitialMapCenter] = useState<
    [number, number] | null
  >(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [selectedPlaceCenter, setSelectedPlaceCenter] = useState<
    [number, number] | null
  >(null);
  const [shouldCenterOnPlace, setShouldCenterOnPlace] =
    useState<boolean>(false);
  const [isLocationPending, setIsLocationPending] = useState<boolean>(true);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoOpenMarker, setAutoOpenMarker] = useState<string | null>(null);
  const [loadingMarkerId, setLoadingMarkerId] = useState<string | null>(null);
  const lastBoundsRef = useRef<string>("");

  const [currentBounds, setCurrentBounds] = useState<Bounds | null>(null);
  const [googleNearbyPlaces, setGoogleNearbyPlaces] = useState<GoogleNearbyPlace[]>([]);
  const hasFetchedGoogleNearbyRef = useRef(false);

  useEffect(() => {
    storage.removeItem(STORAGE_KEYS.PROFILE_TAB);
  }, []);

  useEffect(() => {
    if (isAuthenticated && !hasUserSeenPopup()) {
      setPopupOpen(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setIsMapReady(true);
  }, []);

  const handleSearchResultSelect = async (place: Place) => {
    // Set map center first
    setSelectedPlaceCenter([place.coordinates.lat, place.coordinates.lng]);
    setShouldCenterOnPlace(true);

    // Calculate bounds around the selected place
    const radius = 0.002; // Roughly 200m radius
    const bounds = {
      topLeft: {
        lat: place.coordinates.lat + radius,
        lng: place.coordinates.lng - radius,
      },
      bottomRight: {
        lat: place.coordinates.lat - radius,
        lng: place.coordinates.lng + radius,
      },
    };
    // Fetch places in the new bounds
    setCurrentBounds(bounds);
    await fetchPlacesByBounds(bounds);

    dispatch(setDrawerOpen(true));
    dispatch(setSelectedPlace(place));
    setAutoOpenMarker(place.place_id || null);

    // Clear the auto-open after a short delay
    setTimeout(() => {
      setAutoOpenMarker(null);
    }, 1000);
  };

  useEffect(() => {
    if (userPosition && places.length > 0) {
      const nearest = findNearestPlace(userPosition, places);
      setNearestPlaceId(nearest);
    }
  }, [userPosition, places]);

  useEffect(() => {
    if (userPosition && mapMarkers.length > 0) {
      // Get all places within 20 meters
      const nearbyPlaces = mapMarkers.filter(
        (marker) =>
          calculateDistance(
            userPosition[0],
            userPosition[1],
            marker.position[0],
            marker.position[1]
          ) <= 20
      );

      // Find the closest place to show label
      const closestPlaceId = getClosestPlaceToShowLabel(
        userPosition,
        nearbyPlaces
      );
      setPlaceWithLabel(closestPlaceId);
    }
  }, [userPosition, mapMarkers]);

  useEffect(() => {
    const checkLocationPermission = async () => {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser.");
        setIsLocationPending(false);
        setInitialMapCenter([40.7128, -74.006]); // Default to NYC instead of [0,0]
        return;
      }

      try {
        // First attempt: quick location (may be cached)
        const quickPosition = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: false,
              timeout: 5000,
              maximumAge: 60000, // Accept cached location up to 1 minute old
            });
          }
        );

        const newPosition: [number, number] = [
          quickPosition.coords.latitude,
          quickPosition.coords.longitude,
        ];
        setUserPosition(newPosition);
        setUserAccuracy(quickPosition.coords.accuracy);
        setLocationError(null);

        if (!hasInitialized) {
          setInitialMapCenter(newPosition);
          setHasInitialized(true);
        }
        setIsLocationPending(false);
      } catch (quickError) {
        // If quick location fails, try with high accuracy but longer timeout
        console.log("Quick location failed, trying high accuracy...");

        const watchId = navigator.geolocation.watchPosition(
          (pos) => {
            const newPosition: [number, number] = [
              pos.coords.latitude,
              pos.coords.longitude,
            ];
            setUserPosition(newPosition);
            setUserAccuracy(pos.coords.accuracy);
            setLocationError(null);

            if (!hasInitialized) {
              setInitialMapCenter(newPosition);
              setHasInitialized(true);
            }
            setIsLocationPending(false);

            // Clear watch after getting a good position
            navigator.geolocation.clearWatch(watchId);
          },
          (err) => {
            console.log("Location error:", err);
            // Ignore timeout errors, let the fallback timeout handle complete failure
            if (err.code === 3) return;
            
            // Don't show error immediately - use fallback
            setLocationError(
              "Using default location. Enable GPS for better accuracy."
            );
            setIsLocationPending(false);

            // Set a default center if location fails completely
            if (!hasInitialized) {
              setInitialMapCenter([40.7128, -74.006]); // Default to NYC
              setHasInitialized(true);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          }
        );

        // Fallback timeout
        setTimeout(() => {
          navigator.geolocation.clearWatch(watchId);
          if (!hasInitialized) {
            setLocationError("Location timed out. Using default map view.");
            setInitialMapCenter([40.7128, -74.006]);
            setHasInitialized(true);
            setIsLocationPending(false);
          }
        }, 20000);
      }
    };

    checkLocationPermission();
  }, [hasInitialized]);

  // Handle cleanup on unmount and reset state on mount
  useEffect(() => {
    // Reset state on mount to ensure drawer is closed when re-navigating
    dispatch(clearPlaceFilter());
    dispatch(clearSelectedPlace());

    return () => {
      // Also cleanup on unmount for safety
      dispatch(clearPlaceFilter());
      dispatch(clearSelectedPlace());
    };
  }, [dispatch]);


  const convertPlacesToMarkers = (placesData: Place[]): MarkerData[] => {
    return placesData.map((place) => ({
      position: [place.coordinates.lat, place.coordinates.lng] as [
        number,
        number
      ],
      type: getAccessibilityLevel(place.accessibility || "no_data"),
      label: place.name || `${place.category} - ${place.subcategory}`,
      placeData: place,
    }));
  };

  // Helper function to check if a place is within bounds
  const isPlaceWithinBounds = (
    place: Place,
    bounds: {
      topLeft: { lat: number; lng: number };
      bottomRight: { lat: number; lng: number };
    }
  ): boolean => {
    const { lat, lng } = place.coordinates;
    return (
      lat <= bounds.topLeft.lat &&
      lat >= bounds.bottomRight.lat &&
      lng >= bounds.topLeft.lng &&
      lng <= bounds.bottomRight.lng
    );
  };

  // Stabilize the fetch logic using a ref so the debounced function always calls the latest logic
  // without being recreated itself.
  const fetchPlacesByBoundsLogic = useCallback(
    async (
      bounds: Bounds,
      source: "drag" | "zoom" | "move" | "initial" = "initial"
    ) => {
      const boundsKey = `${bounds.topLeft.lat},${bounds.topLeft.lng},${
        bounds.bottomRight.lat
      },${bounds.bottomRight.lng}-${JSON.stringify(selectedFilter)}`;
      if (boundsKey === lastBoundsRef.current) {
        return;
      }
      const { topLeft, bottomRight } = bounds;

      const params: Record<string, string | number | undefined> = {
        lat1: topLeft.lat,
        long1: topLeft.lng,
        lat2: bottomRight.lat,
        long2: bottomRight.lng,
      };

      if (selectedFilter?.subCategories?.length) {
        params.subcategories = selectedFilter?.subCategories.join(",");
      }

      try {
        setIsLoading(true);

        const response = await api.get(
          ENDPOINTS.PLACES.FETCH_BY_BOUNDS,
          params
        );

        if (Array.isArray(response?.data?.data)) {
          const filteredPlaces = response.data.data.filter(
            (place: Place) => place.is_active
          );

          if (source === "move" || source === "drag") {
            setPlaces((prevPlaces) => {
              const placesWithinBounds = prevPlaces.filter((place) =>
                isPlaceWithinBounds(place, bounds)
              );
              const existingPlaceIds = new Set(
                placesWithinBounds.map((p) => p.place_id)
              );
              const newPlaces = filteredPlaces.filter(
                (place: Place) => !existingPlaceIds.has(place.place_id)
              );
              return [...placesWithinBounds, ...newPlaces];
            });

            setMapMarkers((prevMarkers) => {
              const markersWithinBounds = prevMarkers.filter(
                (marker) =>
                  marker.placeData &&
                  isPlaceWithinBounds(marker.placeData, bounds)
              );
              const existingPlaceIds = new Set(
                markersWithinBounds.map((m) => m.placeData?.place_id)
              );
              const newMarkers = convertPlacesToMarkers(
                filteredPlaces
              ).filter(
                (marker) => !existingPlaceIds.has(marker.placeData?.place_id)
              );
              return [...markersWithinBounds, ...newMarkers];
            });
          } else {
            setPlaces(filteredPlaces);
            setMapMarkers(convertPlacesToMarkers(filteredPlaces));
          }

          lastBoundsRef.current = boundsKey;
        } else {
          console.warn("Invalid response data:", response.data);
        }
      } catch (err) {
        console.error("Error fetching places:", err);
        lastBoundsRef.current = boundsKey;
      } finally {
        setIsLoading(false);
      }
    },
    [api, selectedFilter]
  );

  const fetchLogicRef = useRef(fetchPlacesByBoundsLogic);
  useEffect(() => {
    fetchLogicRef.current = fetchPlacesByBoundsLogic;
  }, [fetchPlacesByBoundsLogic]);

  const fetchPlacesByBounds = useMemo(
    () =>
      debounce(
        (bounds: Bounds, source?: "drag" | "zoom" | "move" | "initial") => {
          fetchLogicRef.current(bounds, source);
        },
        600
      ),
    [] // Empty dependency array: only ONE debounced function instance exists for the lifetime of the component
  );

  const handleBoundsChange = useCallback(
    (bounds: Bounds, source: "drag" | "zoom" | "move") => {
      setCurrentBounds(bounds);
      fetchPlacesByBounds(bounds, source);
    },
    [fetchPlacesByBounds]
  );

  // Trigger fetch when filter changes
  useEffect(() => {
    if (currentBounds && selectedFilter) {
      // Force fetch with current bounds
      fetchPlacesByBounds(currentBounds, "initial");
    }
  }, [selectedFilter, currentBounds, fetchPlacesByBounds]);

  useEffect(() => {
    if (initialMapCenter && isMapReady) {
      // console.log("Initial map center set:", initialMapCenter);
      // Calculate initial bounds based on initialMapCenter
      const radius = 0.01; // ~1km radius
      const initialBounds = {
        topLeft: {
          lat: initialMapCenter[0] + radius,
          lng: initialMapCenter[1] - radius,
        },
        bottomRight: {
          lat: initialMapCenter[0] - radius,
          lng: initialMapCenter[1] + radius,
        },
      };

      setCurrentBounds(initialBounds);
      fetchPlacesByBounds(initialBounds, "initial");
    }
  }, [initialMapCenter, isMapReady, fetchPlacesByBounds]);

  useEffect(() => {
    if (!initialMapCenter || hasFetchedGoogleNearbyRef.current) return;
    hasFetchedGoogleNearbyRef.current = true;

    const fetchGoogleNearby = async () => {
      try {
        const lat = initialMapCenter[0];
        const lng = initialMapCenter[1];
        
        // Round to 3 decimal places (~110m accuracy) for the cache key
        const cacheKey = `google_nearby_${lat.toFixed(3)}_${lng.toFixed(3)}`;
        const cachedData = sessionStorage.getItem(cacheKey);

        if (cachedData) {
          setGoogleNearbyPlaces(JSON.parse(cachedData));
          return;
        }

        const response = await api.post(ENDPOINTS.PLACES.GOOGLE_NEARBY, {
          lat,
          lng,
          radius: 1000,
          maxResultCount: 20,
        });
        if (Array.isArray(response?.data?.data)) {
          setGoogleNearbyPlaces(response.data.data);
          sessionStorage.setItem(cacheKey, JSON.stringify(response.data.data));
        }
      } catch (err) {
        console.error("Error fetching nearby Google places:", err);
      }
    };

    fetchGoogleNearby();
  }, [initialMapCenter, api]);

  const googleOnlyMarkers = useMemo(() => {
    const existingGooglePlaceIds = new Set(
      places.map((p) => p.g_place_id).filter(Boolean),
    );
    return googleNearbyPlaces
      .filter((gp) => gp.location && !existingGooglePlaceIds.has(gp.id))
      .filter((gp) => {
        if (!selectedFilter || !selectedFilter.subCategories || selectedFilter.subCategories.length === 0) {
          return true;
        }
        const googleTypes = gp.types || [];
        return selectedFilter.subCategories.some((sub: string) => googleTypes.includes(sub));
      })
      .map((gp) => {
        const position: [number, number] = [gp.location!.latitude, gp.location!.longitude];
        const iconUrl = getCategoryIconUrl(
          {
            g_place_id: gp.id,
            coordinates: { lat: position[0], lng: position[1] },
            category: gp.types_mapped?.category,
            subcategory: gp.types_mapped?.subcategory,
            accessibility: "no_data",
          },
          "gray",
        );
        return {
          position,
          label: gp.displayName?.text || "Unnamed place",
          googlePlace: gp,
          iconUrl,
        };
      });
  }, [places, googleNearbyPlaces, selectedFilter]);

  const handleGoogleMarkerClick = (googlePlace: GoogleNearbyPlace) => {
    const lat = googlePlace.location?.latitude || 0;
    const lng = googlePlace.location?.longitude || 0;
    const { category_id, category, subcategory_id, subcategory } =
      googlePlace.types_mapped || {
        category_id: null,
        category: "Other",
        subcategory_id: null,
        subcategory: "Other",
      };

    const provisionalPlace: Place = {
      g_place_id: googlePlace.id,
      name: googlePlace.displayName?.text,
      coordinates: { lat, lng },
      category_id: category_id ?? undefined,
      category,
      subcategory_id: subcategory_id ?? undefined,
      subcategory,
      address_text: googlePlace.formattedAddress,
      country_code: googlePlace.address_components_mapped?.country_code,
      city: googlePlace.address_components_mapped?.city,
      postcode: googlePlace.address_components_mapped?.postcode,
    };

    setSelectedPlaceCenter([lat, lng]);
    setShouldCenterOnPlace(true);
    dispatch(setSelectedPlace(provisionalPlace));
    dispatch(setDrawerOpen(true));
  };

  const handleFilterChange = (filter: string) => {
    onFilterChange?.(filter);
  };

  const openDrawer = async (place: Place) => {
    dispatch(setDrawerOpen(true));
    setLoadingMarkerId(place.place_id || null);
    dispatch(setSelectedPlace(place));
    setSelectedPlaceCenter([place.coordinates.lat, place.coordinates.lng]);
    setShouldCenterOnPlace(true);
  };

  const handleCloseDrawer = () => {
    dispatch(setDrawerOpen(false));
    dispatch(clearSelectedPlace());
    setSelectedPlaceCenter(null);
    setShouldCenterOnPlace(false);
  };

  const handleRecenter = () => {
    console.log("Manual recenter requested");
  };

  const handlePopupClose = () => {
    markPopupAsSeen();
    setPopupOpen(false);
  };

  return (
    <>
      <Box sx={componentStyles.container}>
        <AccessibilityGuide open={popupOpen} onClose={handlePopupClose} />
        <Box sx={{ position: "relative" }}>
          <Header
            onFilterChange={handleFilterChange}
            onSearchResultSelect={handleSearchResultSelect}
            userPosition={useMemo(
              () => ({
                coords: {
                  latitude: userPosition?.[0] || 0,
                  longitude: userPosition?.[1] || 0,
                },
              }),
              [userPosition]
            )}
            handleInfoButtonClick={() => {
              setPopupOpen(true), handleCloseDrawer();
            }}
          />
          {/* <Accessibility onFilterChange={handleFilterChange} /> */}

          {/* Overlay when location is not available */}
          {(isLocationPending || locationError || !initialMapCenter) && (
            <Box
              sx={overlayStyles}
              onClick={(e) => e.preventDefault()} // Prevent any click events
            />
          )}
        </Box>

        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 70,
              right: 10,
              zIndex: 1000,
              background: "rgba(255,255,255,0.9)",
              borderRadius: 1,
              p: 1,
            }}
          >
            <Typography variant="caption" color="CUSTOM_COLOR">
              Loading places...
            </Typography>
          </Box>
        )}

        <Box sx={componentStyles.mapContainer}>
          {isLocationPending && (
            <Box sx={componentStyles.loadingOverlay}>
              <CircularProgress size={36} sx={{ color: CUSTOM_COLOR }} />
              <Typography
        variant="h6"
        color="primary"
        gutterBottom
        sx={{
          fontSize: { xs: "1rem", sm: "1.25rem" },
          fontWeight: 600,
          color: CUSTOM_COLOR,
        }}
      >
        Fetching Your Location...
      </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}
              >
                Please wait while we get your location.
              </Typography>
            </Box>
          )}

          {!isLocationPending && initialMapCenter && isMapReady && (
            <MapContainer
              center={initialMapCenter as LatLngExpression}
              zoom={mapZoom}
              style={{ height: "100%", width: "100%" }}
              zoomControl={false}
              attributionControl={true}
              maxZoom={22}
              minZoom={3}
              key={`map-${initialMapCenter[0]}-${initialMapCenter[1]}`}
            >
              <ZoomControl position="topleft" />
              <ChangeView
                center={selectedPlaceCenter}
                zoom={selectedPlaceCenter ? 18 : mapZoom}
                shouldCenter={shouldCenterOnPlace}
              />

              <TileLayer
                url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png${import.meta.env.VITE_CARTO_API_KEY ? `?key=${import.meta.env.VITE_CARTO_API_KEY}` : ''}`}
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>'
                maxZoom={22}
              />

              {/* Add InitialDataLoader component */}
              {/* <InitialDataLoader
                onBoundsChange={fetchPlacesByBounds}
                userPosition={userPosition}
              /> */}

              {userPosition && (
                <Marker
                  position={userPosition as LatLngExpression}
                  icon={createUserIcon(userAccuracy || undefined)}
                >
                  <Popup>You are here</Popup>
                </Marker>
              )}

              {mapMarkers.map((marker, i) => {
                const isNearUser = userPosition
                  ? calculateDistance(
                      userPosition[0],
                      userPosition[1],
                      marker.position[0],
                      marker.position[1]
                    ) <= 20
                  : false;

                const showLabel =
                  isNearUser && marker.placeData?.place_id === placeWithLabel;

                return (
                  <CombinedMarker
                    key={`${marker.placeData?.place_id || i}`}
                    position={marker.position}
                    type={marker.type}
                    label={marker.label}
                    placeData={marker.placeData}
                    onMarkerClick={openDrawer}
                    shouldAutoOpen={
                      autoOpenMarker === marker.placeData?.place_id
                    }
                    isLoading={loadingMarkerId === marker.placeData?.place_id}
                    userPosition={userPosition}
                    isNearest={nearestPlaceId === marker.placeData?.place_id}
                    showLabel={showLabel}
                  />
                );
              })}

              {googleOnlyMarkers.map((gm) => (
                <Marker
                  key={`google-${gm.googlePlace.id}`}
                  position={gm.position}
                  icon={createImageIcon(gm.iconUrl)}
                  eventHandlers={{
                    click: () => handleGoogleMarkerClick(gm.googlePlace),
                  }}
                />
              ))}

              <SimplifiedBoundsWatcher
                onBoundsChange={handleBoundsChange}
              />
              <MapController
                userPosition={userPosition}
                onRecenter={handleRecenter}
              />
            </MapContainer>
          )}
          {!isLocationPending && (locationError || !initialMapCenter) && (
            <Box sx={componentStyles.loadingOverlay}>
              <MyLocationIcon
                sx={{
                  fontSize: { xs: 36, sm: 48 },
                  color: CUSTOM_COLOR,
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                color="primary"
                gutterBottom
                sx={{
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  fontWeight: 600,
                }}
              >
                {locationError?.includes("Using default")
                  ? "Location Services"
                  : "Location Access Required"}
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  mb: 3,
                  textAlign: "center",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                {/* {locationError?.includes("Using default")
                  ? "You can still explore the map and search for places."
                  : "Please enable location services for the best experience."} */}
                {"Please enable location services for the best experience."}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => window.location.reload()}
                sx={{
                  background: "linear-gradient(90deg, #8a2be2, #6a5acd)",
                  borderRadius: "25px",
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.2 },
                  textTransform: "none",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  fontWeight: 500,
                }}
              >
                {locationError?.includes("Using default")
                  ? "Retry Location"
                  : "Enable Location"}
              </Button>
            </Box>
          )}
        </Box>
        <BottomNavBar />
      </Box>

      <FilterDrawer
        userPosition={useMemo(
          () => ({
            coords: {
              latitude: userPosition?.[0] || 0,
              longitude: userPosition?.[1] || 0,
            },
          }),
          [userPosition]
        )}
      />
    </>
  );
};

export default Explore;
